import { useEffect, useId, useState } from "react";
import { useLocation } from "react-router-dom";
import type { UserPreferences, UserPreferencesUpdate } from "@starsach/shared-types";
import { useLeaveOrganisation, useMyMemberships } from "../api/hooks";
import { useStepUp } from "../auth/StepUpProvider";
import { useAuth } from "../auth/AuthProvider";
import { Card, ErrorState, LoadingState, MutationError, PageHeader, btnSecondary, inputClass, sentence } from "../components/ui";
import { detectedTimeZone, formatDate, formatTime, landingPages, resolveTimeFormat } from "../lib/preferences";
import { Hint } from "../components/Hint";
import { useFormat } from "../lib/useFormat";
import { useVisibleEnvironments } from "../components/Environment";
import { visibleModules } from "../lib/modules";
import { MyAccess } from "./account/MyAccess";
import { SecuritySection } from "./account/SecuritySection";

const THEMES: Array<{ value: UserPreferences["theme"]; label: string }> = [
  { value: "system", label: "Follow the device" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];
/** #449: each choice is named and followed by today's date in that form, so the choice is legible. */
const DATE_FORMATS: Array<{ value: UserPreferences["dateFormat"]; label: string }> = [
  { value: "auto", label: "Device default" },
  { value: "iso", label: "ISO 8601 (YYYY-MM-DD)" },
  { value: "us", label: "US (MM/DD/YYYY)" },
  { value: "intl", label: "International (DD/MM/YYYY)" },
  { value: "us-text", label: "US written" },
  { value: "intl-text", label: "International written" },
];

/** #449: the clock is its own choice, never derived from the date format; each option shows the time now. */
const TIME_FORMATS: Array<{ value: UserPreferences["timeFormat"]; label: string }> = [
  { value: "auto", label: "Follow the device" },
  { value: "12h", label: "12-hour" },
  { value: "24h", label: "24-hour" },
];

const MOTIONS: Array<{ value: UserPreferences["motion"]; label: string }> = [
  { value: "auto", label: "Follow the device" },
  { value: "standard", label: "Standard" },
  { value: "reduced", label: "Reduced" },
];

/** Requirements section 13: one page, sections in the order Profile, Preferences, Security, Access, Privacy - Security and Privacy arrive with their own issues. */
export function AccountPage() {
  const { me, preferences, updatePreferences } = useAuth();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);
  const [saving, setSaving] = useState<keyof UserPreferences>();
  const [saved, setSaved] = useState<keyof UserPreferences>();
  const [error, setError] = useState<unknown>();
  const zones = timeZones();
  const detected = detectedTimeZone();
  const ids = { theme: useId(), zone: useId(), format: useId(), clock: useId(), landing: useId(), motion: useId() };
  const fmt = useFormat();

  const save = async (patch: UserPreferencesUpdate) => {
    const key = Object.keys(patch)[0] as keyof UserPreferences;
    setSaving(key);
    setSaved(undefined);
    setError(undefined);
    try {
      await updatePreferences(patch);
      setSaved(key);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(undefined);
    }
  };
  const status = (key: keyof UserPreferences) => (saving === key ? <span className="ml-2 text-xs text-neutral-500">Saving…</span> : saved === key ? <span role="status" className="ml-2 text-xs text-green-700 dark:text-green-300">Saved</span> : null);
  const now = new Date().toISOString();

  return (
    <>
      <PageHeader title="Account" subtitle={me ? `${me.email ?? me.subject} · ${me.viewAs ? "Gairm staff" : sentence(me.role)}` : undefined} />
      {me?.viewAs && (
        <p role="status" className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          These are your own preferences, not the customer's, and a view-as tab is read-only. Change them from your own tab.
        </p>
      )}
      <div className="space-y-6">
        <ProfileSection />
        <Card title="Preferences">
          <div className="grid gap-5 md:grid-cols-2">
            <label htmlFor={ids.theme} className="text-sm">
              <span className="block font-medium">Theme</span>
              <select id={ids.theme} className={inputClass} value={preferences.theme} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ theme: e.target.value as UserPreferences["theme"] })}>
                {THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {status("theme")}
            </label>

            <label htmlFor={ids.motion} className="text-sm">
              <span className="block font-medium">Motion</span>
              <select id={ids.motion} className={inputClass} value={preferences.motion} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ motion: e.target.value as UserPreferences["motion"] })}>
                {MOTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Reduced turns off transitions that only decorate; spinners and progress keep moving.</span>
              {status("motion")}
            </label>

            <label htmlFor={ids.zone} className="text-sm">
              <span className="block font-medium">Time zone</span>
              <select id={ids.zone} className={inputClass} value={preferences.timeZone} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ timeZone: e.target.value })}>
                <option value="auto">Detect from this device ({detected})</option>
                {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Everything is stored in UTC and shown in this zone: alarms, audit, journeys, quotas.</span>
              {status("timeZone")}
            </label>

            <label htmlFor={ids.format} className="text-sm">
              <span className="block font-medium">Date format</span>
              <select id={ids.format} className={inputClass} value={preferences.dateFormat} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ dateFormat: e.target.value as UserPreferences["dateFormat"] })}>
                {DATE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label} · {formatDate(now, { ...preferences, dateFormat: f.value })}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Right now reads as <time dateTime={now}>{fmt.when(now)}</time>.</span>
              {status("dateFormat")}
            </label>

            <div className="text-sm">
              <span className="flex items-center gap-1">
                <label htmlFor={ids.clock} className="font-medium">Time format</label>
                <Hint id="account.time-format" label="time format" />
              </span>
              <select id={ids.clock} className={inputClass} value={preferences.timeFormat} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ timeFormat: e.target.value as UserPreferences["timeFormat"] })}>
                {TIME_FORMATS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.value === "auto" ? `${t.label} (${resolveTimeFormat({ timeFormat: "auto" }) === "12h" ? "12-hour" : "24-hour"})` : t.label} · {formatTime(now, { ...preferences, timeFormat: t.value })}
                  </option>
                ))}
              </select>
              {status("timeFormat")}
            </div>

            <label htmlFor={ids.landing} className="text-sm">
              <span className="block font-medium">Start on</span>
              <select id={ids.landing} className={inputClass} value={preferences.landingPage} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ landingPage: e.target.value })}>
                {landingPages(me?.role).map((l) => <option key={l.path} value={l.path}>{l.label}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Where each sign-in takes you.</span>
              {status("landingPage")}
            </label>
          </div>
          {error !== undefined && <ErrorState error={error} />}
        </Card>
        <FocusEnvironmentsSection />
        <SecuritySection />
        <AccessSection />
      </div>
    </>
  );
}

/** Section 13.2: first and last name, from which the display name is made. The email remains who you are to the audit log. */
function ProfileSection() {
  const { me, updateProfile } = useAuth();
  const [first, setFirst] = useState(me?.firstName ?? "");
  const [last, setLast] = useState(me?.lastName ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<unknown>();
  const id = useId();
  const dirty = first.trim() !== (me?.firstName ?? "") || last.trim() !== (me?.lastName ?? "");
  if (me?.identityProvider) {
    return (
      <Card title="Profile">
        <p className="text-sm"><span className="font-medium">Name:</span> {me.displayName ?? "not provided"}</p>
        <p className="mt-1 text-xs text-neutral-500">Your name comes from {me.identityProvider} and is updated each time you sign in. Change it there, then sign in again. The audit trail keeps your email ({me.email ?? me.subject}) as who acted.</p>
      </Card>
    );
  }
  return (
    <Card title="Profile">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setState("saving");
          setError(undefined);
          updateProfile(first, last)
            .then(() => setState("saved"))
            .catch((err) => {
              setError(err);
              setState("idle");
            });
        }}
      >
        <label htmlFor={`${id}-first`} className="text-sm">
          <span className="block font-medium">First name</span>
          <input id={`${id}-first`} className={`${inputClass} w-44`} value={first} maxLength={40} autoComplete="given-name" disabled={Boolean(me?.viewAs)} onChange={(e) => { setFirst(e.target.value); setState("idle"); }} />
        </label>
        <label htmlFor={`${id}-last`} className="text-sm">
          <span className="block font-medium">Last name</span>
          <input id={`${id}-last`} className={`${inputClass} w-44`} value={last} maxLength={40} autoComplete="family-name" disabled={Boolean(me?.viewAs)} onChange={(e) => { setLast(e.target.value); setState("idle"); }} />
        </label>
        <button type="submit" className={btnSecondary} disabled={!dirty || state === "saving" || Boolean(me?.viewAs)}>
          {state === "saving" ? "Saving…" : "Save name"}
        </button>
        {state === "saved" && <span role="status" className="text-xs text-green-700 dark:text-green-300">Saved</span>}
        <span className="basis-full text-xs text-neutral-500">How the portal addresses you{me?.displayName ? ` - shown as "${me.displayName}"` : ""}. The audit trail keeps your email ({me?.email ?? me?.subject}) as who acted.</span>
      </form>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/**
 * Section 13.4 [30] - where do I have access, and as what. A role is held
 * per environment and never rides the token, so without this a user has
 * no way to see their own access at all. Read-only: every grant and
 * revoke still goes through the environment's Access tab.
 */
function AccessSection() {
  const { me, signOut } = useAuth();
  const q = useMyMemberships();
  const leave = useLeaveOrganisation();
  const { ensureFresh } = useStepUp();
  const [confirmLeave, setConfirmLeave] = useState(false);
  return (
    <div id="access" className="scroll-mt-6 space-y-4">
      <Card title="Access">
        {/* minmax(0,1fr) and wrapping values: a long address or id never widens the page at phone width (#416). */}
        <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-6 gap-y-2 text-sm">
          <dt className="text-neutral-600 dark:text-neutral-400">Signed in as</dt>
          <dd className="min-w-0 break-words text-neutral-900 dark:text-neutral-100">{me?.email ?? me?.subject ?? "—"}</dd>
          <dt className="text-neutral-600 dark:text-neutral-400">Organization</dt>
          <dd className={me?.organisationId ? "min-w-0 break-all font-mono text-neutral-900 dark:text-neutral-100" : "min-w-0 break-words text-neutral-900 dark:text-neutral-100"}>{me?.organisationId ?? (me?.role === "internal-team" ? "Gairm (internal team)" : "—")}</dd>
          <dt className="text-neutral-600 dark:text-neutral-400">Access tier</dt>
          <dd className="min-w-0 break-words text-neutral-900 dark:text-neutral-100">{me?.role ? sentence(me.role) : "—"}</dd>
        </dl>
        <p className="mt-3 text-xs text-neutral-500">A role is held per environment, so authority in one says nothing about anywhere else. Changes are made on an environment's Access module by whoever may edit people there.</p>
        {me?.role === "customer" && !me.viewAs && (
          <div className="mt-4 border-t border-neutral-200 pt-4 dark:border-neutral-800">
            {!confirmLeave ? (
              <button type="button" className={btnSecondary} onClick={() => setConfirmLeave(true)}>Leave this organization</button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm">Your roles are revoked and your sign-in disabled. A manager can reinstate you within thirty days.</span>
                <button type="button" className={btnSecondary} disabled={leave.isPending} onClick={() => void ensureFresh().then(() => leave.mutate(undefined, { onSuccess: () => void signOut() }), () => undefined)}>
                  {leave.isPending ? "Leaving…" : "Yes, leave"}
                </button>
                <button type="button" className={btnSecondary} onClick={() => setConfirmLeave(false)}>Cancel</button>
              </div>
            )}
            <MutationError of={leave} className="mt-3" />
          </div>
        )}
      </Card>
      {q.isPending && <LoadingState label="Loading your access" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && <MyAccess data={q.data} />}
    </div>
  );
}

/** Every IANA zone the browser knows, with the common ones the same as anywhere else; a short list when the API is missing. */
function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  const all = intl.supportedValuesOf?.("timeZone") ?? ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Dublin", "Asia/Tokyo", "Australia/Sydney"];
  return all.includes("UTC") ? all : ["UTC", ...all];
}

/**
 * Section 13.1: the focus environment per module - which environment
 * Alarms, Journeys, Flow configuration and the rest open on from the
 * rail. Production for the operational modules and Development for Flow
 * configuration is the usual shape; a module with no choice opens on
 * the environment last looked at.
 */
function FocusEnvironmentsSection() {
  const { me, preferences, updatePreferences } = useAuth();
  const envs = useVisibleEnvironments();
  const [busy, setBusy] = useState<string>();
  const [saved, setSaved] = useState<string>();
  const [error, setError] = useState<unknown>();
  const modules = visibleModules(me).filter((m) => m.environmentScoped);
  const options = envs.data?.customers ?? [];

  const choose = async (moduleId: string, environmentId: string) => {
    setBusy(moduleId);
    setSaved(undefined);
    setError(undefined);
    const next = { ...preferences.focusEnvironments };
    if (environmentId) next[moduleId] = environmentId;
    else delete next[moduleId];
    try {
      await updatePreferences({ focusEnvironments: next });
      setSaved(moduleId);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <Card title="Focus environments">
      <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">
        Which environment each module opens on when you pick it from the rail - Alarms and Journeys on Production, Flow configuration on Development, say. A module with no choice opens on the environment you last looked at. Focus's Yesterday row opens on production - each organization's last environment in its chain - unless you choose otherwise here.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {/* The owner's decision of 2026-09-27: Focus's Yesterday row opens on production unless a default is kept here. */}
        {(() => {
          const chosen = preferences.focusEnvironments["focus"] ?? "";
          const stale = chosen && chosen !== "all" && envs.data && !options.some((e) => e.environmentId === chosen);
          return (
            <label className="text-sm">
              <span className="block font-medium">Focus: yesterday</span>
              <select className={inputClass} value={chosen} disabled={Boolean(me?.viewAs) || busy === "focus" || !envs.data} onChange={(e) => void choose("focus", e.target.value)} aria-label="Focus environment for Focus: yesterday">
                <option value="">Production</option>
                <option value="all">All environments</option>
                {options.map((e) => (
                  <option key={e.environmentId} value={e.environmentId}>{e.friendlyName}</option>
                ))}
                {stale && <option value={chosen}>{chosen} (no longer visible to you)</option>}
              </select>
              {busy === "focus" ? <span className="ml-2 text-xs text-neutral-500">Saving…</span> : saved === "focus" ? <span role="status" className="ml-2 text-xs text-green-700 dark:text-green-300">Saved</span> : null}
            </label>
          );
        })()}
        {modules.map((m) => {
          const chosen = preferences.focusEnvironments[m.id] ?? "";
          const stale = chosen && envs.data && !options.some((e) => e.environmentId === chosen);
          return (
            <label key={m.id} className="text-sm">
              <span className="block font-medium">{m.label}</span>
              <select className={inputClass} value={chosen} disabled={Boolean(me?.viewAs) || busy === m.id || !envs.data} onChange={(e) => void choose(m.id, e.target.value)} aria-label={`Focus environment for ${m.label}`}>
                <option value="">Where I last looked</option>
                {options.map((e) => (
                  <option key={e.environmentId} value={e.environmentId}>{e.friendlyName}</option>
                ))}
                {stale && <option value={chosen}>{chosen} (no longer visible to you)</option>}
              </select>
              {busy === m.id ? <span className="ml-2 text-xs text-neutral-500">Saving…</span> : saved === m.id ? <span role="status" className="ml-2 text-xs text-green-700 dark:text-green-300">Saved</span> : null}
            </label>
          );
        })}
      </div>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}
